import mediator from "@tvg/mediator";
import { v4 as uuid } from "uuid";
import { RaceProgram, RaceVideoFeedback } from "@tvg/ts-types/Race";
import service from "@tvg/api";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import { addToast, removeToast } from "../../../store/actions";

export interface GetVideoStreamPopout {
  race: RaceProgram;
  isReplay?: boolean;
}

export const getLiveStreamButtonLabels = (
  startingSoon: boolean,
  watchReplay: boolean,
  isLiveStreaming: boolean,
  notAvailable?: boolean,
  playedOnNBC?: boolean
) => {
  if (startingSoon) {
    return {
      text: "Starting soon",
      qaLabel: "starting-soon-button"
    };
  }
  if (watchReplay) {
    return {
      text: "Watch Replay",
      qaLabel: "watch-replay-button"
    };
  }
  if (isLiveStreaming) {
    return {
      text: "Watch Live",
      qaLabel: "watch-live-button"
    };
  }

  if (notAvailable) {
    return {
      text: "Not Available",
      qaLabel: "not-available-button"
    };
  }

  if (playedOnNBC) {
    return {
      text: "Shown on NBC",
      qaLabel: "shown-on-nbc-button"
    };
  }

  return { text: "", qaLabel: "" };
};

export const getVideoStreamPopout = async ({
  race,
  isReplay = false
}: GetVideoStreamPopout) => {
  const width = "640";
  const height = "480";

  try {
    const data = await service.rcn.getVideoStreamUrl({
      streamName: isReplay
        ? race.video?.replayFileName
        : race.video?.streams[0] ?? "",
      trackName: race.track?.trackName ?? "",
      width,
      height,
      isReplay,
      isHd: race.video?.isStreamHighDefinition
    });
    const windowFeatures = `width=${width},height=${height}`;

    // This is needed for safari especially on ipad pro, because of the open call being inside a async function
    return setTimeout(() => {
      window.open(data.tvgUrl, "", windowFeatures);
    });
  } catch (e) {
    return e;
  }
};

export const watchReplay = (videoFeedback?: RaceVideoFeedback) =>
  [
    RaceVideoFeedback.AVAILABLE_REPLAY,
    RaceVideoFeedback.NOT_AVAILABLE_REPLAY,
    RaceVideoFeedback.FINISHED_REPLAY,
    RaceVideoFeedback.STARTING_SOON_REPLAY
  ].includes(videoFeedback as RaceVideoFeedback);

export const startingSoon = (videoFeedback?: RaceVideoFeedback) =>
  [
    RaceVideoFeedback.STARTING_SOON_REPLAY,
    RaceVideoFeedback.STARTING_SOON_NO_REPLAY
  ].includes(videoFeedback as RaceVideoFeedback);

export const liveStreaming = (videoFeedback?: RaceVideoFeedback) =>
  [
    RaceVideoFeedback.AVAILABLE_REPLAY,
    RaceVideoFeedback.AVAILABLE_NO_REPLAY
  ].includes(videoFeedback as RaceVideoFeedback);

export const onClickWatchVideo = async (
  dispatch: Function,
  isLogged: boolean,
  race: RaceProgram,
  videoFeedback: RaceVideoFeedback | undefined,
  isAccountCompliant?: boolean
) => {
  try {
    if (isLogged) {
      if (isAccountCompliant) {
        await getVideoStreamPopout({
          race,
          isReplay: watchReplay(videoFeedback)
        });
      } else {
        onOpenComplianceModal();
      }
    } else {
      mediator.base.dispatch({
        type: "TVG_LOGIN:OPEN_LOGIN_MODAL",
        payload: {
          callback: async (data?: { status: string }) => {
            if (!data || data.status !== "error") {
              await getVideoStreamPopout({
                race,
                isReplay: watchReplay(videoFeedback)
              });
            }
          }
        }
      });
    }
  } catch (e) {
    const toastId = uuid();

    dispatch(
      addToast({
        id: toastId,
        variant: "error",
        title: "",
        message: "Unable to open video",
        qaLabel: "video-error-toast",
        onHide: () => dispatch(removeToast(toastId)),
        onDismissPress: () => dispatch(removeToast(toastId))
      })
    );
  }
};
